// コメント本文の表示コンポーネント。
// - CommentMessageBody: 本文（生の Markdown テキスト）を、付箋・テキストと同じ行単位ブロック記法
//   （見出し # 〜 ######・箇条書き - / * / +、ネスト対応）で整形表示する。スタイル・表示テキストの
//   決定は markdown-text.ts（付箋・テキストの MarkdownTextView と同じ単一の真実）へ集約し、
//   各行の本文はコメント専用の LinkifiedCommentText でリンク化する。
//   記法を含まない本文は全行 plain の div となり、従来の見た目（pre-wrap のプレーン表示 +
//   リンク化）と一致する。保存は付箋・テキストと同じく生 Markdown のまま（表示時のみ整形）。
// - LinkifiedCommentText: コメント本文の URL 自動リンク化（従来 comment-thread-popover.tsx 内に
//   あったものを、Markdown 整形表示との共用のため本ファイルへ移設）。

import {
  markdownLineStyleFor,
  markdownLineTextFor,
  parseMarkdownTextLines,
} from "../../../lib/markdown-text";
import { isExternalHttpUrl, splitTextIntoLinkSegments } from "../../../lib/linkify-text";
import { ExternalLinkIcon } from "../objects/linkified-text";
import { useCanvasStore } from "../store/canvas-store";

interface Props {
  readonly text: string;
}

// コメント本文（生の Markdown テキスト）の整形表示。
// 付箋・テキストの MarkdownTextView と同じ行構造（1 行 = 1 div、スタイルは markdown-text.ts の
// 単一の真実）で描画するが、リンクはキャンバスオブジェクト用 LinkifiedText（250ms 遅延オープン・
// ダブルクリック編集への譲歩）ではなく、コメント専用の LinkifiedCommentText（<a> の標準挙動 +
// stopPropagation）を使う。見出しの fontSize / 箇条書きインデントは em 単位のため、コメントの
// フォントサイズ（14px）に相対でスケールする。
export function CommentMessageBody({ text }: Props): JSX.Element {
  const lines = parseMarkdownTextLines(text);
  return (
    <>
      {lines.map((line, index) => {
        const style = markdownLineStyleFor(line);
        const lineText = markdownLineTextFor(line);
        return (
          // 行は本文位置と 1:1 対応の静的リストで並べ替えが起きないため index key で問題ない。
          <div key={index} style={style}>
            {/* 空行は <br> で 1 行分の高さを維持する（空 div は高さ 0 になるため）。 */}
            {lineText.length > 0 ? <LinkifiedCommentText text={lineText} /> : <br />}
          </div>
        );
      })}
    </>
  );
}

// コメント本文の URL を自動リンク化して描画する。
// キャンバス上のオブジェクト用 LinkifiedText とは異なり、コメントはダブルクリック編集が無いため
// 遅延オープンは行わず、<a> の標準挙動で開く。内部/外部の判定・外部アイコン・bare URL の
// 省略表示（title でフル URL 確認可）はオブジェクト側と同じ仕様・同じ CSS クラスへ揃える。
// 参照専用（埋め込み iframe）では内部リンクの同一タブ遷移が iframe の内容を置き換えて
// しまうため、オブジェクト側 openUrl と同様に readOnly 時は内部リンクも新規タブで開く。
export function LinkifiedCommentText({ text }: { readonly text: string }): JSX.Element {
  const readOnly = useCanvasStore((s) => s.readOnly);
  const segments = splitTextIntoLinkSegments(text);
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === "text" || !segment.url) {
          // key はセグメント列のインデックス。text は再レンダーのたびに再分割されるため安定。
          return <span key={index}>{segment.value}</span>;
        }
        const url = segment.url;
        const external = isExternalHttpUrl(url);
        const newTab = external || readOnly;
        // 表示テキストが URL そのもの（自動リンク化）の場合のみ 1 行省略表示の対象にする。
        const bareUrl = segment.value === url;
        const className = [
          "canvas-linkified-link",
          "comment-linkified-link",
          ...(bareUrl ? ["canvas-linkified-link--url"] : []),
        ].join(" ");
        return (
          <a
            key={index}
            className={className}
            href={url}
            target={newTab ? "_blank" : undefined}
            rel={newTab ? "noopener noreferrer" : undefined}
            title={bareUrl ? url : undefined}
            // リンククリックがポップオーバーの pointerdown 系ハンドラやキャンバスへ波及しないようにする。
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            {bareUrl ? (
              <span className="canvas-linkified-link-label">{segment.value}</span>
            ) : (
              segment.value
            )}
            {external ? <ExternalLinkIcon /> : null}
          </a>
        );
      })}
    </>
  );
}
